import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  askDrugQuestion,
  buildDrugMonographPdfUrl,
  fetchDrugMonograph,
  fetchUserDrugs,
  preloadDrugMonographs,
  saveUserDrug,
  searchDrugProducts,
  warmDrugMonograph,
} from '../services/drugMonographService';
import { fetchMedications } from '../services/medicationService';
import {
  canUseWebLLM,
  ensureWebLLMEngine,
  generateWebLLMReply,
  getWebLLMModelLabel,
  subscribeToWebLLMProgress,
} from '../services/webllmService';
import { buildPatientDrugOptions } from '../utils/patientDrugHelpers';

const SYSTEM_PROMPT = `You are answering questions about a Health Canada drug monograph for patients and caregivers.
Only use the provided context.
Write in plain, patient-friendly language.
Prefer short bullet points over long paragraphs.
Summarize faithfully instead of copying long passages word-for-word.
If you must use a medical term, briefly explain it in simpler words.
If the answer is not found, say exactly: 'Not found in monograph'.
Always mention the source section if possible.
Do not provide medical advice.`;
const SECTION_HINTS = {
  'Adverse Reactions': ['adverse reaction', 'adverse reactions', 'side effect', 'side effects'],
  Contraindications: ['contraindication', 'contraindications', 'should not take'],
  Warnings: ['warning', 'warnings', 'precaution', 'precautions'],
  Indications: ['indication', 'indications', 'used for', 'treats'],
  Dosage: ['dosage', 'dose', 'dosing', 'administration'],
};

function formatProgress(progress) {
  if (!progress) {
    return 'Preparing local AI model...';
  }

  const label = progress.text || progress.status || progress.type || 'Preparing local AI model...';
  if (typeof progress.progress === 'number') {
    return `${label} (${Math.round(progress.progress * 100)}%)`;
  }

  return label;
}

function buildContext(chunks) {
  return (Array.isArray(chunks) ? chunks : [])
    .map((chunk, index) => {
      const section = chunk?.section || `Chunk ${index + 1}`;
      return `Section: ${section}\n${chunk?.text || ''}`;
    })
    .join('\n\n');
}

function summarizeSections(chunks) {
  return Array.from(
    new Set(
      (Array.isArray(chunks) ? chunks : [])
        .map((chunk) => String(chunk?.section || '').trim())
        .filter(Boolean),
    ),
  );
}

function splitIntoSentences(text) {
  return String(text || '')
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
}

function looksLikeBulletList(text) {
  return /(^|\n)\s*[-*•]\s+/.test(String(text || ''))
    || /(^|\n)\s*\d+\.\s+/.test(String(text || ''));
}

function normalizeQuestionText(input) {
  return String(input || '')
    .toLowerCase()
    .replace(/\bside effects?\b/g, 'adverse reactions')
    .replace(/\badverse effects?\b/g, 'adverse reactions')
    .replace(/\badverese\b/g, 'adverse')
    .replace(/\s+/g, ' ')
    .trim();
}

function inferPreferredSections(question) {
  const normalizedQuestion = normalizeQuestionText(question);

  return Object.entries(SECTION_HINTS)
    .filter(([, hints]) => hints.some((hint) => normalizedQuestion.includes(hint)))
    .map(([section]) => section);
}

function scoreChunkForQuestion(chunk, question) {
  const normalizedQuestion = normalizeQuestionText(question);
  const combinedText = `${chunk?.section || ''} ${chunk?.text || ''}`.toLowerCase();
  const targetSections = inferPreferredSections(question);
  const tokens = normalizedQuestion
    .split(/[^a-z0-9]+/)
    .map((token) => token.trim())
    .filter((token) => token.length >= 3);

  let score = targetSections.includes(chunk?.section) ? 30 : 0;
  for (const token of tokens) {
    if (combinedText.includes(token)) {
      score += 2;
    }
    if (String(chunk?.section || '').toLowerCase().includes(token)) {
      score += 4;
    }
  }

  return score;
}

function rankChunksForQuestion(chunks, question) {
  return (Array.isArray(chunks) ? chunks : [])
    .map((chunk) => ({
      ...chunk,
      __rankScore: scoreChunkForQuestion(chunk, question),
    }))
    .sort((a, b) => b.__rankScore - a.__rankScore)
    .map(({ __rankScore, ...chunk }) => chunk);
}

function extractRelevantSnippet(text, question) {
  const normalizedQuestion = normalizeQuestionText(question);
  const lines = String(text || '')
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => !/^(section:|part\s+[ivx]+:)/i.test(line));

  if (lines.length === 0) {
    return '';
  }

  const bestLines = lines
    .map((line) => ({
      line,
      score: normalizedQuestion
        .split(/[^a-z0-9]+/)
        .filter((token) => token.length >= 3)
        .reduce((total, token) => total + (line.toLowerCase().includes(token) ? 1 : 0), 0),
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 4)
    .map((entry) => entry.line);

  const snippet = (bestLines.some((entry) => entry) ? bestLines : lines.slice(0, 4))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  return snippet.slice(0, 900).trim();
}

function formatAnswerAsPatientBullets(answer, sourceSections = []) {
  const cleaned = String(answer || '').trim();
  if (!cleaned || /not found in monograph/i.test(cleaned)) {
    return cleaned || 'Not found in monograph';
  }

  const sectionLine = sourceSections.length > 0
    ? `Source section: ${sourceSections.join(', ')}`
    : '';

  if (looksLikeBulletList(cleaned)) {
    if (!sectionLine || /source section:/i.test(cleaned)) {
      return cleaned;
    }
    return `${cleaned}\n\n${sectionLine}`.trim();
  }

  const sentences = splitIntoSentences(cleaned);
  if (sentences.length === 0) {
    return sectionLine ? `${cleaned}\n\n${sectionLine}`.trim() : cleaned;
  }

  const summary = sentences[0];
  const bulletCandidates = (sentences.length > 1 ? sentences.slice(1, 5) : sentences.slice(0, 4))
    .map((sentence) => sentence.replace(/^[-*•]\s*/, '').trim())
    .filter(Boolean);

  const bulletBlock = bulletCandidates
    .map((sentence) => `- ${sentence}`)
    .join('\n');

  return [
    summary,
    bulletBlock,
    sectionLine,
  ]
    .filter(Boolean)
    .join('\n\n')
    .trim();
}

function buildPatientFriendlyFallbackAnswer(chunks, question) {
  const rankedChunks = rankChunksForQuestion(chunks, question);
  const bestChunk = rankedChunks[0];
  if (!bestChunk?.text) {
    return '';
  }

  const snippet = extractRelevantSnippet(bestChunk.text, question);
  if (!snippet) {
    return '';
  }

  const sentences = splitIntoSentences(snippet).slice(0, 4);
  if (sentences.length === 0) {
    return `Source section: ${bestChunk.section || 'Monograph'}`;
  }

  return [
    `Here are the key points the monograph lists:`,
    '',
    ...sentences.map((sentence) => `- ${sentence}`),
    '',
    `Source section: ${bestChunk.section || 'Monograph'}`,
  ].join('\n');
}

function statusTheme(status) {
  if (status === 'ready') {
    return {
      background: '#ecfdf5',
      border: '#a7f3d0',
      color: '#166534',
    };
  }

  if (status === 'embedding_failed') {
    return {
      background: '#fffbeb',
      border: '#fde68a',
      color: '#92400e',
    };
  }

  return {
    background: '#eff6ff',
    border: '#bfdbfe',
    color: '#1d4ed8',
  };
}

export default function DrugMonographs({ user }) {
  const userId = user?.id || user?.username || '';
  const username = user?.username || '';
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [savedDrugs, setSavedDrugs] = useState([]);
  const [preloadStatus, setPreloadStatus] = useState('');
  const [preloadedMonographs, setPreloadedMonographs] = useState({});
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [monograph, setMonograph] = useState(null);
  const [isMonographExpanded, setIsMonographExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectionNotice, setSelectionNotice] = useState('');
  const [monographLoading, setMonographLoading] = useState(false);
  const [monographError, setMonographError] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState('');
  const [engineReady, setEngineReady] = useState(false);
  const [engineStatus, setEngineStatus] = useState('Waiting to load local AI model...');
  const [engineError, setEngineError] = useState('');
  const bottomRef = useRef(null);
  const monographRequestIdRef = useRef(0);

  useEffect(() => {
    let active = true;

    const loadSaved = async () => {
      if (!userId && !username) {
        return;
      }

      try {
        const [savedSelectionsResult, medicationsResult] = await Promise.allSettled([
          userId ? fetchUserDrugs(userId) : Promise.resolve([]),
          username ? fetchMedications(username) : Promise.resolve([]),
        ]);

        const savedSelections = savedSelectionsResult.status === 'fulfilled'
          ? savedSelectionsResult.value
          : [];
        const medications = medicationsResult.status === 'fulfilled'
          ? medicationsResult.value
          : [];

        if (active) {
          setSavedDrugs(
            buildPatientDrugOptions({
              medications: Array.isArray(medications) ? medications : [],
              savedDrugs: Array.isArray(savedSelections) ? savedSelections : [],
            }),
          );
        }
      } catch {
        if (active) {
          setSavedDrugs([]);
        }
      }
    };

    void loadSaved();

    return () => {
      active = false;
    };
  }, [userId, username]);

  useEffect(() => {
    let active = true;

    if (savedDrugs.length === 0) {
      setPreloadStatus('');
      setPreloadedMonographs({});
      return () => {
        active = false;
      };
    }

    let completed = 0;
    let failures = 0;
    setPreloadStatus(`Preloading monographs for ${savedDrugs.length} saved medication${savedDrugs.length === 1 ? '' : 's'}...`);

    void preloadDrugMonographs(savedDrugs, {
      concurrency: 2,
      onSettled: ({ drug, payload, error }) => {
        if (!active) {
          return;
        }

        completed += 1;
        if (error) {
          failures += 1;
        } else if (payload?.monograph) {
          setPreloadedMonographs((prev) => ({
            ...prev,
            [drug.din]: payload.monograph,
          }));
        }

        if (completed >= savedDrugs.length) {
          const successCount = completed - failures;
          setPreloadStatus(
            failures > 0
              ? `Preloaded ${successCount} of ${savedDrugs.length} medication monograph${savedDrugs.length === 1 ? '' : 's'}.`
              : `Preloaded ${savedDrugs.length} medication monograph${savedDrugs.length === 1 ? '' : 's'}.`,
          );
          return;
        }

        setPreloadStatus(`Preloading monographs... ${completed}/${savedDrugs.length}`);
      },
    });

    return () => {
      active = false;
    };
  }, [savedDrugs]);

  useEffect(() => {
    let active = true;
    const trimmed = query.trim();

    if (!trimmed || (trimmed.length < 2 && !/^\d{8}$/.test(trimmed))) {
      setResults([]);
      setSearchLoading(false);
      setSearchError('');
      return () => {
        active = false;
      };
    }

    setSearchLoading(true);
    setSearchError('');

    const timer = setTimeout(async () => {
      try {
        const nextResults = await searchDrugProducts(trimmed);
        if (active) {
          setResults(nextResults);
        }
      } catch (error) {
        if (active) {
          setResults([]);
          setSearchError(error?.message || 'Unable to search drug products right now.');
        }
      } finally {
        if (active) {
          setSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query]);

  useEffect(() => {
    const unsubscribe = subscribeToWebLLMProgress((progress) => {
      setEngineStatus(formatProgress(progress));
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function initializeEngine() {
    if (engineReady) {
      return true;
    }

    if (!canUseWebLLM()) {
      setEngineError(
        'This browser does not support WebGPU. Use a recent Chrome or Edge build to run WebLLM locally.',
      );
      setEngineStatus('WebGPU not available in this browser.');
      return false;
    }

    setEngineError('');
    setEngineStatus('Preparing local AI model...');

    try {
      await ensureWebLLMEngine();
      const modelLabel = getWebLLMModelLabel();
      setEngineReady(true);
      setEngineStatus(
        modelLabel ? `Local AI ready: ${modelLabel}` : 'Local AI model is ready.',
      );
      return true;
    } catch (error) {
      setEngineError(error?.message || 'Unable to initialize the local AI model.');
      setEngineStatus('Local AI model failed to load.');
      return false;
    }
  }

  async function handleSelectDrug(drug) {
    setSelectedDrug(drug);
    setMonograph(null);
    setIsMonographExpanded(false);
    setMonographError('');
    setChatError('');
    setSelectionNotice('');
    setMessages([
      {
        role: 'assistant',
        text: preloadedMonographs[drug.din]
          ? `Selected ${drug.productName}. Opening the preloaded Health Canada monograph...`
          : `Selected ${drug.productName}. Fetching the Health Canada monograph automatically...`,
      },
    ]);

    if (userId) {
      try {
        await saveUserDrug(userId, drug);
        setSavedDrugs((prev) => {
          const withoutDuplicate = prev.filter((entry) => String(entry.din) !== String(drug.din));
          return [
            {
              userId,
              din: drug.din,
              productName: drug.productName,
              activeIngredientGroupNumber: drug.activeIngredientGroupNumber || '',
              source: drug.source || 'saved',
              medicationStatus: drug.medicationStatus || 'current',
              createdAt: new Date().toISOString(),
            },
            ...withoutDuplicate,
          ];
        });
        setSelectionNotice('Drug saved to the patient medication list for faster monograph access.');
      } catch (error) {
        setSelectionNotice(error?.message || 'Selected drug locally, but saving the selection failed.');
      }
    }

    void loadMonographForDrug(drug);
  }

  async function loadMonographForDrug(drug, options = {}) {
    if (!drug?.din) {
      return;
    }

    const requestId = monographRequestIdRef.current + 1;
    monographRequestIdRef.current = requestId;

    setMonographLoading(true);
    setMonographError('');
    setChatError('');

    try {
      const payload = options.forceRefresh === true
        ? await fetchDrugMonograph(drug.din, { forceRefresh: true })
        : await warmDrugMonograph(drug.din);

      if (requestId !== monographRequestIdRef.current) {
        return;
      }

      setMonograph(payload?.monograph || null);
      if (payload?.monograph) {
        setPreloadedMonographs((prev) => ({
          ...prev,
          [drug.din]: payload.monograph,
        }));
      }
      setIsMonographExpanded(false);

      const status = payload?.status || 'ready';
      if (['product_not_found', 'pdf_not_found', 'parse_failed'].includes(status)) {
        setMessages([
          {
            role: 'assistant',
            text: payload?.monograph?.lastError || `Monograph pipeline status: ${status}.`,
          },
        ]);
        return;
      }

      const intro = status === 'embedding_failed'
        ? `The monograph is available for ${drug.productName}. Chunks were stored, but embeddings were unavailable, so retrieval may fall back to text matching.`
        : `The monograph is ready for ${drug.productName}. Ask about indications, contraindications, warnings, dosage, or adverse reactions.`;

      setMessages([
        {
          role: 'assistant',
          text: intro,
          warning: payload?.embeddingError || '',
          sources: [],
        },
      ]);

      void initializeEngine();
    } catch (error) {
      if (requestId !== monographRequestIdRef.current) {
        return;
      }
      setMonograph(null);
      setMonographError(error?.message || 'Failed to fetch the monograph.');
    } finally {
      if (requestId === monographRequestIdRef.current) {
        setMonographLoading(false);
      }
    }
  }

  async function handleViewMonograph(options = {}) {
    if (!selectedDrug?.din) {
      return;
    }

    await loadMonographForDrug(selectedDrug, options);
  }

  async function handleAsk() {
    const question = chatInput.trim();
    if (!question || !selectedDrug?.din || chatLoading) {
      return;
    }

    const ready = await initializeEngine();
    if (!ready) {
      return;
    }

    setChatLoading(true);
    setChatError('');
    setMessages((prev) => [...prev, { role: 'user', text: question }, { role: 'assistant', text: '' }]);
    setChatInput('');

    try {
      const retrieval = await askDrugQuestion(selectedDrug.din, question);
      const chunks = Array.isArray(retrieval?.chunks) ? retrieval.chunks : [];
      const rankedChunks = rankChunksForQuestion(chunks, question);
      const contextChunks = rankedChunks.slice(0, 3);

      if (chunks.length === 0) {
        setMessages((prev) => {
          const next = [...prev];
          next[next.length - 1] = {
            role: 'assistant',
            text: 'Not found in monograph',
            sources: [],
            retrievalMode: retrieval?.retrievalMode || 'lexical_fallback',
            warning: retrieval?.warning || '',
          };
          return next;
        });
        return;
      }

      const context = buildContext(contextChunks);
      const fallbackAnswer = buildPatientFriendlyFallbackAnswer(contextChunks, question);
      let finalReply = '';
      let messageWarning = retrieval?.warning || '';

      try {
        const reply = await generateWebLLMReply({
          messages: [
            {
              role: 'system',
              content: SYSTEM_PROMPT,
            },
            {
              role: 'user',
              content: `Use the context below to answer for a patient or caregiver.

Instructions:
- Use plain language.
- Prefer 3 to 5 short bullet points.
- Keep the answer concise and easy to understand.
- Mention the source section at the end.
- Do not copy long passages from the monograph.

Context:
${context}

Question:
${question}`,
            },
          ],
          temperature: 0.2,
          topP: 0.85,
          maxTokens: 260,
          onChunk: (partialReply) => {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = {
                role: 'assistant',
                text: partialReply,
              };
              return next;
            });
          },
        });

        finalReply = formatAnswerAsPatientBullets(
          reply || '',
          summarizeSections(contextChunks),
        );
      } catch (modelError) {
        if (fallbackAnswer) {
          finalReply = fallbackAnswer;
          messageWarning = [
            messageWarning,
            modelError?.message ? `WebLLM fallback used: ${modelError.message}` : 'WebLLM fallback used.',
          ]
            .filter(Boolean)
            .join(' ');
        } else {
          throw modelError;
        }
      }

      if ((!finalReply || /not found in monograph/i.test(finalReply)) && fallbackAnswer) {
        finalReply = fallbackAnswer;
      }

      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: 'assistant',
          text: finalReply || 'Not found in monograph',
          sources: summarizeSections(contextChunks),
          retrievalMode: retrieval?.retrievalMode || 'vector',
          warning: messageWarning,
        };
        return next;
      });
    } catch (error) {
      const fallback = error?.message || 'Failed to answer with monograph context.';
      setChatError(fallback);
      setMessages((prev) => {
        const next = [...prev];
        next[next.length - 1] = {
          role: 'assistant',
          text: fallback,
        };
        return next;
      });
    } finally {
      setChatLoading(false);
    }
  }

  const inputDisabled =
    !selectedDrug
    || !monograph
    || (monograph?.chunkCount || 0) < 1
    || Boolean(engineError)
    || monographLoading;

  const pdfUrl = monograph?.localPdfUrl || buildDrugMonographPdfUrl(selectedDrug?.din || '');
  const monographTone = statusTheme(monograph?.status || 'pending');

  return (
    <div style={{ maxWidth: '1160px', margin: '0 auto', paddingBottom: '40px' }}>
      <div style={{ marginBottom: '18px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', color: '#0f172a' }}>Drug Monograph Workspace</h2>
        <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '14px', lineHeight: 1.6 }}>
          Patient medications saved with product name and DIN are preloaded here first, so grounded local monograph Q&A opens faster.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px, 360px) minmax(0, 1fr)', gap: '16px' }}>
        <section style={panelStyle}>
          <div style={sectionTitleStyle}>Your saved medications</div>
          <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#64748b', lineHeight: 1.6 }}>
            Choose from medications already saved to the patient account. Their monographs are warmed in the background to reduce chat wait time.
          </p>

          {preloadStatus && <div style={noticeCardStyle}>{preloadStatus}</div>}
          {selectionNotice && <div style={noticeCardStyle}>{selectionNotice}</div>}

          <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '280px', overflowY: 'auto' }}>
            {savedDrugs.length === 0 ? (
              <div style={{ ...mutedCardStyle, marginTop: 0 }}>
                No account medications with DIN selections yet. Add one in Medication Tracker, or search the drug catalog below.
              </div>
            ) : (
              savedDrugs.map((drug) => {
                const warmedMonograph = preloadedMonographs[drug.din];
                return (
                  <button
                    key={`saved-${drug.din}`}
                    type="button"
                    onClick={() => handleSelectDrug(drug)}
                    style={{
                      ...listButtonStyle,
                      borderColor: selectedDrug?.din === drug.din ? 'var(--teal)' : '#dbe4f0',
                      background: selectedDrug?.din === drug.din ? '#effcf9' : 'white',
                    }}
                  >
                    <div style={{ fontWeight: '700', color: '#0f172a', textAlign: 'left' }}>{drug.productName}</div>
                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#475569', textAlign: 'left' }}>
                      DIN {drug.din}
                    </div>
                    <div style={{ marginTop: '3px', fontSize: '11px', color: '#64748b', textAlign: 'left' }}>
                      {drug.source === 'medication'
                        ? `${drug.medicationStatus === 'past' ? 'Past' : 'Current'} medication`
                        : 'Saved monograph selection'}
                      {drug.dosage ? ` - ${drug.dosage}` : ''}
                    </div>
                    <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
                      <span style={savedDrugStatusStyle}>
                        {warmedMonograph ? 'Monograph preloaded' : 'Preloading in background'}
                      </span>
                      {warmedMonograph?.status && (
                        <span style={{ fontSize: '11px', color: '#475569', fontWeight: '700', textTransform: 'capitalize' }}>
                          {warmedMonograph.status}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div style={{ marginTop: '18px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
            <label style={labelStyle}>Search other drug products</label>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by product name or enter DIN"
              style={inputStyle}
            />

            {searchError && <div style={errorCardStyle}>{searchError}</div>}

            <div style={{ marginTop: '12px' }}>
              <div style={sectionTitleStyle}>Results</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px', maxHeight: '240px', overflowY: 'auto' }}>
                {searchLoading ? (
                  <div style={mutedCardStyle}>Searching drug_products...</div>
                ) : results.length === 0 ? (
                  <div style={mutedCardStyle}>
                    {query.trim().length >= 2
                      ? 'No matching drug products found.'
                      : 'Start typing a product name or 8-digit DIN.'}
                  </div>
                ) : (
                  results.map((drug) => (
                    <button
                      key={`${drug.din}-${drug.productName}`}
                      type="button"
                      onClick={() => handleSelectDrug(drug)}
                      style={{
                        ...listButtonStyle,
                        borderColor: selectedDrug?.din === drug.din ? 'var(--teal)' : '#dbe4f0',
                        background: selectedDrug?.din === drug.din ? '#effcf9' : 'white',
                      }}
                    >
                      <div style={{ fontWeight: '700', color: '#0f172a', textAlign: 'left' }}>{drug.productName}</div>
                      <div style={{ marginTop: '4px', fontSize: '12px', color: '#475569', textAlign: 'left' }}>
                        DIN {drug.din}
                      </div>
                      <div style={{ marginTop: '3px', fontSize: '11px', color: '#64748b', textAlign: 'left' }}>
                        Active ingredient group {drug.activeIngredientGroupNumber || 'N/A'}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          <div style={{ marginTop: '16px' }}>
            <div style={sectionTitleStyle}>Quick picks</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
              {savedDrugs.length === 0 ? (
                <div style={{ ...mutedCardStyle, marginTop: 0, width: '100%' }}>Saved medication shortcuts will appear here.</div>
              ) : (
                savedDrugs.slice(0, 8).map((drug) => (
                  <button
                    key={`shortcut-${drug.din}`}
                    type="button"
                    onClick={() => handleSelectDrug(drug)}
                    style={savedDrugButtonStyle}
                  >
                    {drug.productName}
                  </button>
                ))
              )}
            </div>
          </div>

          <div style={{ marginTop: '18px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
            <div style={sectionTitleStyle}>Selected drug</div>
            {selectedDrug ? (
              <div style={{ marginTop: '10px', display: 'grid', gap: '10px' }}>
                <div style={selectedDrugCardStyle}>
                  <div style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a' }}>
                    {selectedDrug.productName}
                  </div>
                  <div style={{ marginTop: '4px', fontSize: '13px', color: '#475569' }}>
                    DIN {selectedDrug.din}
                  </div>
                </div>
                <div style={mutedCardStyle}>
                  {monographLoading
                    ? 'Fetching the monograph automatically...'
                    : preloadedMonographs[selectedDrug.din]
                      ? 'This medication was preloaded from the patient account and is ready to open.'
                      : 'The monograph fetch starts automatically when you select a drug.'}
                </div>
                <button
                  type="button"
                  onClick={() => handleViewMonograph({ forceRefresh: true })}
                  disabled={monographLoading}
                  style={{
                    ...secondaryButtonStyle,
                    opacity: monographLoading ? 0.7 : 1,
                    cursor: monographLoading ? 'wait' : 'pointer',
                  }}
                >
                  Refresh Monograph
                </button>
              </div>
              ) : (
                <div style={{ ...mutedCardStyle, marginTop: '10px' }}>
                Choose one of the patient medications above to open a preloaded monograph, or search for another drug.
                </div>
              )}
          </div>
        </section>

        <section style={{ display: 'grid', gap: '16px' }}>
          <div style={panelStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div>
                <div style={sectionTitleStyle}>Monograph</div>
              </div>
              {monograph && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <div
                    style={{
                      background: monographTone.background,
                      border: `1px solid ${monographTone.border}`,
                      color: monographTone.color,
                      borderRadius: '999px',
                      padding: '7px 12px',
                      fontSize: '12px',
                      fontWeight: '700',
                      textTransform: 'capitalize',
                    }}
                  >
                    {monograph.status}
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsMonographExpanded((current) => !current)}
                    style={compactToggleButtonStyle}
                  >
                    {isMonographExpanded ? 'Hide Monograph' : 'View Monograph'}
                  </button>
                </div>
              )}
            </div>

            {monographError && <div style={{ ...errorCardStyle, marginTop: '14px' }}>{monographError}</div>}

            {monograph ? (
              <>
                {!isMonographExpanded ? (
                  <div style={{ ...mutedCardStyle, marginTop: '14px' }}>
                    Monograph loaded for {monograph.productName || selectedDrug?.productName || 'this drug'}.
                    Expand to view the PDF and parsed details.
                  </div>
                ) : (
                  <>
                    <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
                      <div style={metaCardStyle}>
                        <div style={metaLabelStyle}>Product</div>
                        <div style={metaValueStyle}>{monograph.productName || selectedDrug?.productName || 'Unknown'}</div>
                      </div>
                      <div style={metaCardStyle}>
                        <div style={metaLabelStyle}>DIN</div>
                        <div style={metaValueStyle}>{monograph.din}</div>
                      </div>
                      <div style={metaCardStyle}>
                        <div style={metaLabelStyle}>Chunks stored</div>
                        <div style={metaValueStyle}>{monograph.chunkCount || 0}</div>
                      </div>
                      <div style={metaCardStyle}>
                        <div style={metaLabelStyle}>Sections</div>
                        <div style={metaValueStyle}>
                          {(monograph.sectionsDetected || []).length > 0
                            ? monograph.sectionsDetected.join(', ')
                            : 'Fallback chunking'}
                        </div>
                      </div>
                    </div>

                    {monograph.lastError && (
                      <div style={{ ...noticeCardStyle, marginTop: '12px' }}>
                        {monograph.lastError}
                      </div>
                    )}

                    <div style={{ marginTop: '16px', border: '1px solid #dbe4f0', borderRadius: '14px', overflow: 'hidden', minHeight: '420px', background: '#f8fafc' }}>
                      <iframe
                        title="Drug monograph PDF viewer"
                        src={pdfUrl}
                        style={{ width: '100%', height: '420px', border: 'none', background: 'white' }}
                      />
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '12px' }}>
                      <a href={pdfUrl} target="_blank" rel="noreferrer" style={{ color: '#0f766e', fontWeight: '700' }}>
                        Open PDF in a new tab
                      </a>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div style={{ ...mutedCardStyle, marginTop: '14px' }}>
                The PDF viewer will appear here after you fetch a monograph.
              </div>
            )}
          </div>

          <div style={panelStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div>
                <div style={sectionTitleStyle}>Grounded WebLLM Q&A</div>
                <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#64748b' }}>
                  The backend retrieves the most relevant monograph chunks, then WebLLM answers locally using only that context.
                </p>
              </div>
              <div
                style={{
                  maxWidth: '360px',
                  fontSize: '12px',
                  color: engineError ? '#991b1b' : '#0f766e',
                  background: engineError ? '#fef2f2' : '#ecfeff',
                  border: `1px solid ${engineError ? '#fecaca' : '#a5f3fc'}`,
                  borderRadius: '10px',
                  padding: '10px 12px',
                }}
              >
                {engineError || engineStatus}
              </div>
            </div>

            {chatError && <div style={{ ...errorCardStyle, marginTop: '14px' }}>{chatError}</div>}

            <div style={{ marginTop: '16px', minHeight: '260px', maxHeight: '420px', overflowY: 'auto', border: '1px solid #dbe4f0', borderRadius: '14px', background: 'white', padding: '16px' }}>
              {messages.length === 0 ? (
                <div style={{ color: '#64748b', fontSize: '14px', lineHeight: 1.6 }}>
                  Fetch a monograph to start asking grounded questions.
                </div>
              ) : (
                messages.map((message, index) => (
                  <div
                    key={`${message.role}-${index}`}
                    style={{
                      marginBottom: '12px',
                      display: 'flex',
                      justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <div
                      style={{
                        maxWidth: '90%',
                        background: message.role === 'user' ? '#dcfce7' : '#f8fafc',
                        border: `1px solid ${message.role === 'user' ? '#86efac' : '#e2e8f0'}`,
                        borderRadius: '14px',
                        padding: '12px 14px',
                        color: '#0f172a',
                        fontSize: '14px',
                        lineHeight: 1.55,
                      }}
                    >
                      {message.role === 'assistant' ? (
                        <ReactMarkdown>{message.text || '...'}</ReactMarkdown>
                      ) : (
                        message.text
                      )}

                      {Array.isArray(message.sources) && message.sources.length > 0 && (
                        <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {message.sources.map((section) => (
                            <span key={`${section}-${index}`} style={sourceChipStyle}>
                              {section}
                            </span>
                          ))}
                        </div>
                      )}

                      {message.retrievalMode === 'lexical_fallback' && (
                        <div style={{ marginTop: '8px', fontSize: '11px', color: '#92400e' }}>
                          Retrieval fallback: lexical match
                        </div>
                      )}

                      {message.warning && (
                        <div style={{ marginTop: '8px', fontSize: '11px', color: '#92400e' }}>
                          {message.warning}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}

              {chatLoading && (
                <div style={{ color: '#64748b', fontSize: '13px' }}>
                  Generating a grounded answer locally...
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
              <input
                value={chatInput}
                onChange={(event) => setChatInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleAsk();
                  }
                }}
                disabled={inputDisabled}
                placeholder={
                  selectedDrug && monograph
                    ? 'Ask a question about the monograph...'
                    : 'Fetch a monograph to enable grounded Q&A'
                }
                style={{
                  ...inputStyle,
                  flex: 1,
                  opacity: inputDisabled ? 0.65 : 1,
                }}
              />
              <button
                type="button"
                onClick={handleAsk}
                disabled={inputDisabled || chatLoading}
                style={{
                  ...primaryButtonStyle,
                  minWidth: '120px',
                  opacity: inputDisabled || chatLoading ? 0.7 : 1,
                  cursor: inputDisabled || chatLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {chatLoading ? 'Asking...' : 'Ask'}
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

const panelStyle = {
  background: 'rgba(255, 255, 255, 0.92)',
  border: '1px solid rgba(209, 213, 219, 0.9)',
  borderRadius: '18px',
  padding: '18px',
  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
};

const sectionTitleStyle = {
  fontSize: '16px',
  fontWeight: '800',
  color: '#0f172a',
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontSize: '13px',
  fontWeight: '700',
  color: '#334155',
};

const inputStyle = {
  width: '100%',
  borderRadius: '12px',
  border: '1px solid #cbd5e1',
  background: 'white',
  padding: '12px 14px',
  fontSize: '14px',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

const primaryButtonStyle = {
  border: 'none',
  borderRadius: '12px',
  background: 'linear-gradient(135deg, #0f766e 0%, #0ea5a4 100%)',
  color: 'white',
  fontSize: '14px',
  fontWeight: '800',
  padding: '12px 16px',
  boxShadow: '0 10px 24px rgba(15, 118, 110, 0.25)',
};

const secondaryButtonStyle = {
  border: '1px solid #99f6e4',
  borderRadius: '12px',
  background: '#f0fdfa',
  color: '#0f766e',
  fontSize: '14px',
  fontWeight: '800',
  padding: '12px 16px',
};

const compactToggleButtonStyle = {
  border: '1px solid #cbd5e1',
  borderRadius: '999px',
  background: 'white',
  color: '#0f172a',
  fontSize: '12px',
  fontWeight: '700',
  padding: '7px 12px',
  cursor: 'pointer',
};

const listButtonStyle = {
  border: '1px solid #dbe4f0',
  borderRadius: '14px',
  padding: '12px',
  background: 'white',
  cursor: 'pointer',
};

const savedDrugButtonStyle = {
  border: '1px solid #bfdbfe',
  background: '#eff6ff',
  color: '#1d4ed8',
  borderRadius: '999px',
  padding: '7px 12px',
  fontSize: '12px',
  fontWeight: '700',
  cursor: 'pointer',
};

const savedDrugStatusStyle = {
  border: '1px solid #bfdbfe',
  background: '#eff6ff',
  color: '#1d4ed8',
  borderRadius: '999px',
  padding: '4px 8px',
  fontSize: '11px',
  fontWeight: '700',
};

const selectedDrugCardStyle = {
  border: '1px solid #bae6fd',
  background: 'linear-gradient(135deg, #f0fdfa 0%, #eff6ff 100%)',
  borderRadius: '14px',
  padding: '14px',
};

const mutedCardStyle = {
  border: '1px dashed #cbd5e1',
  borderRadius: '14px',
  background: '#f8fafc',
  color: '#64748b',
  fontSize: '13px',
  padding: '14px',
};

const noticeCardStyle = {
  marginTop: '12px',
  borderRadius: '12px',
  border: '1px solid #bfdbfe',
  background: '#eff6ff',
  color: '#1d4ed8',
  padding: '10px 12px',
  fontSize: '13px',
};

const errorCardStyle = {
  marginTop: '12px',
  borderRadius: '12px',
  border: '1px solid #fecaca',
  background: '#fef2f2',
  color: '#991b1b',
  padding: '10px 12px',
  fontSize: '13px',
};

const metaCardStyle = {
  border: '1px solid #e2e8f0',
  background: '#f8fafc',
  borderRadius: '12px',
  padding: '12px',
};

const metaLabelStyle = {
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: '#64748b',
  fontWeight: '700',
};

const metaValueStyle = {
  marginTop: '6px',
  fontSize: '13px',
  color: '#0f172a',
  fontWeight: '700',
  lineHeight: 1.5,
};

const sourceChipStyle = {
  borderRadius: '999px',
  border: '1px solid #a7f3d0',
  background: '#ecfdf5',
  color: '#166534',
  padding: '4px 8px',
  fontSize: '11px',
  fontWeight: '700',
};
